import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Users, TrendingUp, Landmark, Receipt, Eye, Mail, Flower2, BookOpen,
  UserCog, CreditCard, ArrowUpRight, ArrowDownRight, Minus, ChevronDown,
  ChevronUp, Sparkles
} from 'lucide-react';
import api from '../../lib/api';

const fmt = (n) => new Intl.NumberFormat('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

function MetricCard({ icon: Icon, label, children, accent = false }) {
  return (
    <div className={`rounded-2xl border p-5 ${accent ? 'border-[#c9a227]/30 bg-[#c9a227]/5' : 'border-[#e8e2d8] bg-white'}`}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-[#c9a227]" strokeWidth={1.5} />
        <span className="text-xs uppercase tracking-wider text-[#5a4a2a]/50 font-medium">{label}</span>
      </div>
      {children}
    </div>
  );
}

function RevenueChart({ data }) {
  const max = Math.max(...data.map(d => d.revenue), 1);
  return (
    <div className="flex items-end gap-2 h-32 mt-3">
      {data.map((d, i) => {
        const h = (d.revenue / max) * 100;
        return (
          <div key={i} className="flex-1 flex flex-col items-center gap-1">
            <span className="text-[8px] text-[#5a4a2a]/40 font-mono">{d.revenue > 0 ? `€${Math.round(d.revenue)}` : ''}</span>
            <div className="w-full flex flex-col justify-end" style={{ height: '80px' }}>
              <div
                className="w-full rounded-t bg-gradient-to-t from-[#c9a227] to-[#e8c547] hover:from-[#b8922a] hover:to-[#c9a227] transition-all min-h-[2px]"
                style={{ height: `${Math.max(h, 2)}%` }}
                title={`${d.month_name}: €${fmt(d.revenue)} → Neto: €${fmt(d.net)}`}
              />
            </div>
            <span className="text-[9px] text-[#5a4a2a]/40">{d.month_name?.slice(0, 3)}</span>
          </div>
        );
      })}
    </div>
  );
}

function MiniBarChart({ data }) {
  const max = Math.max(...data.map(d => d.views), 1);
  return (
    <div className="flex items-end gap-0.5 h-16 mt-2">
      {data.map((d, i) => (
        <div key={i} className="flex-1 flex flex-col items-center">
          <div
            className="w-full rounded-t bg-[#c9a227]/25 hover:bg-[#c9a227]/40 transition-colors min-h-[1px]"
            style={{ height: `${(d.views / max) * 100}%` }}
            title={`${d.label}: ${d.views}`}
          />
        </div>
      ))}
    </div>
  );
}

function ChangeBadge({ pct }) {
  if (pct === 0) return <span className="inline-flex items-center text-xs text-[#5a4a2a]/40"><Minus className="w-3 h-3 mr-0.5" />0%</span>;
  return pct > 0
    ? <span className="inline-flex items-center text-xs text-emerald-600"><ArrowUpRight className="w-3 h-3 mr-0.5" />+{pct}%</span>
    : <span className="inline-flex items-center text-xs text-rose-500"><ArrowDownRight className="w-3 h-3 mr-0.5" />{pct}%</span>;
}

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBreakdown, setShowBreakdown] = useState(false);

  useEffect(() => {
    api.get('/admin/ceo-dashboard')
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#faf8f4] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#c9a227] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#faf8f4] flex items-center justify-center">
        <p className="text-[#5a4a2a]/50">No se pudo cargar el dashboard</p>
      </div>
    );
  }

  const { period, billing, abundancia, monthly_breakdown, community, taxes, expenses, analytics } = data;

  const yearBreakdownTotal = (monthly_breakdown || []).reduce((acc, m) => acc + m.net, 0);

  return (
    <div className="min-h-screen bg-[#faf8f4]" data-testid="admin-dashboard">
      <div className="max-w-6xl mx-auto px-6 py-8">

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-heading text-[#5a4a2a] mb-1">Visión Clara</h1>
          <p className="text-sm text-[#5a4a2a]/40">{period.month_name} {period.year}</p>
        </div>

        {/* Row 1: Abundancia Neta — Hero card */}
        <div className="rounded-2xl border-2 border-[#c9a227]/40 bg-gradient-to-br from-[#c9a227]/5 to-[#faf8f4] p-6 mb-4" data-testid="abundancia-neta-card">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-5 h-5 text-[#c9a227]" strokeWidth={1.5} />
            <span className="text-xs uppercase tracking-wider text-[#c9a227] font-semibold">Mi Abundancia Neta</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div>
              <p className="text-xs text-[#5a4a2a]/40 mb-1">Este mes (neto)</p>
              <p className="text-2xl font-heading text-[#5a4a2a]" data-testid="net-month">{fmt(abundancia.month_net)} <span className="text-sm text-[#5a4a2a]/30">EUR</span></p>
            </div>
            <div>
              <p className="text-xs text-[#5a4a2a]/40 mb-1">Acumulado anual (neto)</p>
              <p className="text-2xl font-heading text-[#5a4a2a]" data-testid="net-year">{fmt(abundancia.year_net)} <span className="text-sm text-[#5a4a2a]/30">EUR</span></p>
            </div>
            <div>
              <p className="text-xs text-[#5a4a2a]/40 mb-1">Media facturado / mes</p>
              <p className="text-2xl font-heading text-[#5a4a2a]" data-testid="avg-billing">{fmt(abundancia.avg_monthly_billing)} <span className="text-sm text-[#5a4a2a]/30">EUR</span></p>
            </div>
            <div>
              <p className="text-xs text-[#5a4a2a]/40 mb-1">Media neto / mes</p>
              <p className="text-2xl font-heading text-[#c9a227]" data-testid="avg-net">{fmt(abundancia.avg_monthly_net)} <span className="text-sm text-[#c9a227]/50">EUR</span></p>
            </div>
          </div>
        </div>

        {/* Row 2: Billing */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <MetricCard icon={TrendingUp} label="Facturación Mensual" accent>
            <p className="text-3xl font-heading text-[#5a4a2a]" data-testid="billing-month">{fmt(billing.month)} EUR</p>
            <div className="mt-1">
              <ChangeBadge pct={billing.month_change_pct} />
              <span className="text-xs text-[#5a4a2a]/30 ml-2">vs mes anterior</span>
            </div>
          </MetricCard>

          <MetricCard icon={Landmark} label="Facturación Anual">
            <p className="text-3xl font-heading text-[#5a4a2a]" data-testid="billing-year">{fmt(billing.year)} EUR</p>
            <div className="mt-2 h-2 bg-[#e8e2d8] rounded-full overflow-hidden">
              <div className="h-full bg-[#c9a227] rounded-full transition-all" style={{ width: `${Math.min(100, billing.year_pct)}%` }} />
            </div>
            <p className="text-xs text-[#5a4a2a]/30 mt-1">{billing.year_pct}% de la meta anual ({fmt(billing.annual_goal)} EUR)</p>
          </MetricCard>
        </div>

        {/* Row 3: Revenue Chart + Visits */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="md:col-span-2">
            <MetricCard icon={TrendingUp} label="Ingresos por mes">
              {monthly_breakdown && monthly_breakdown.length > 0 ? (
                <RevenueChart data={monthly_breakdown} />
              ) : (
                <p className="text-xs text-[#5a4a2a]/30 italic py-6 text-center">Los datos se irán acumulando mes a mes</p>
              )}
            </MetricCard>
          </div>
          <MetricCard icon={Eye} label={`Visitas web — 14 días`}>
            <p className="text-2xl font-heading text-[#5a4a2a]" data-testid="views-14d">{analytics.views_14d}</p>
            <MiniBarChart data={analytics.daily_views} />
            <div className="mt-2 space-y-1">
              {analytics.top_pages.slice(0, 4).map((p, i) => (
                <div key={i} className="flex justify-between items-center">
                  <span className="text-[10px] text-[#5a4a2a]/50 truncate max-w-[70%]">{p.path}</span>
                  <span className="text-[10px] font-medium text-[#5a4a2a]/70">{p.views}</span>
                </div>
              ))}
            </div>
          </MetricCard>
        </div>

        {/* Row 4: Monthly Breakdown (expandable) */}
        <div className="rounded-2xl border border-[#e8e2d8] bg-white mb-4 overflow-hidden">
          <button
            onClick={() => setShowBreakdown(!showBreakdown)}
            className="w-full flex items-center justify-between px-5 py-4 hover:bg-[#faf8f4] transition-colors"
            data-testid="toggle-breakdown"
          >
            <div className="flex items-center gap-2">
              <Receipt className="w-4 h-4 text-[#c9a227]" strokeWidth={1.5} />
              <span className="text-xs uppercase tracking-wider text-[#5a4a2a]/50 font-medium">Desglose Real Mensual {period.year}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-[#5a4a2a]/40">Neto acumulado: <span className="font-heading text-[#5a4a2a]">{fmt(yearBreakdownTotal)} EUR</span></span>
              {showBreakdown ? <ChevronUp className="w-4 h-4 text-[#5a4a2a]/30" /> : <ChevronDown className="w-4 h-4 text-[#5a4a2a]/30" />}
            </div>
          </button>
          {showBreakdown && (
            <div className="px-5 pb-5">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-[#5a4a2a]/40">
                    <th className="text-left py-2 font-medium">Mes</th>
                    <th className="text-right py-2 font-medium">Facturado</th>
                    <th className="text-right py-2 font-medium">Gastos</th>
                    <th className="text-right py-2 font-medium">IVA</th>
                    <th className="text-right py-2 font-medium">IRPF</th>
                    <th className="text-right py-2 font-medium">Neto</th>
                  </tr>
                </thead>
                <tbody>
                  {(monthly_breakdown || []).map((m) => (
                    <tr key={m.month} className="border-t border-[#e8e2d8]/50" data-testid={`breakdown-${m.month}`}>
                      <td className="py-2.5 text-[#5a4a2a]/70">{m.month_name}</td>
                      <td className="py-2.5 text-right text-[#5a4a2a]">{fmt(m.revenue)}</td>
                      <td className="py-2.5 text-right text-rose-400">{m.expenses > 0 ? `-${fmt(m.expenses)}` : '—'}</td>
                      <td className="py-2.5 text-right text-[#5a4a2a]/50">{fmt(m.vat)}</td>
                      <td className="py-2.5 text-right text-[#5a4a2a]/50">{fmt(m.irpf)}</td>
                      <td className={`py-2.5 text-right font-heading ${m.net >= 0 ? 'text-[#5a4a2a]' : 'text-rose-500'}`}>{fmt(m.net)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-[#c9a227]/30">
                    <td className="py-3 font-heading text-[#5a4a2a]">Total {period.year}</td>
                    <td className="py-3 text-right font-heading text-[#5a4a2a]">{fmt((monthly_breakdown || []).reduce((a, m) => a + m.revenue, 0))}</td>
                    <td className="py-3 text-right font-heading text-rose-400">{fmt((monthly_breakdown || []).reduce((a, m) => a + m.expenses, 0))}</td>
                    <td className="py-3 text-right font-heading text-[#5a4a2a]/50">{fmt((monthly_breakdown || []).reduce((a, m) => a + m.vat, 0))}</td>
                    <td className="py-3 text-right font-heading text-[#5a4a2a]/50">{fmt((monthly_breakdown || []).reduce((a, m) => a + m.irpf, 0))}</td>
                    <td className="py-3 text-right font-heading text-[#c9a227]">{fmt(yearBreakdownTotal)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>

        {/* Row 5: Community */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
          <MetricCard icon={Users} label="Alumnas Totales">
            <p className="text-3xl font-heading text-[#5a4a2a]" data-testid="total-students">{community.total_students}</p>
            <p className="text-xs text-[#c9a227] mt-1">+{community.new_students_month} nuevas este mes</p>
          </MetricCard>

          <MetricCard icon={Flower2} label="Membresías Campus">
            <p className="text-3xl font-heading text-[#5a4a2a]" data-testid="campus-active">{community.campus_active}</p>
            <p className="text-xs text-[#5a4a2a]/30 mt-1">de {community.campus_total} total</p>
          </MetricCard>

          <MetricCard icon={Mail} label="Suscriptores Email">
            <p className="text-3xl font-heading text-[#5a4a2a]" data-testid="email-subs">{community.total_email_subs}</p>
            <p className="text-xs text-[#c9a227] mt-1">+{community.new_email_subs} nuevos este mes</p>
          </MetricCard>
        </div>

        {/* Row 6: Taxes — Trimestral + Anual */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <MetricCard icon={Receipt} label="IVA (21%)">
            <div className="flex justify-between items-end">
              <div>
                <p className="text-xs text-[#5a4a2a]/40 mb-0.5">{taxes.quarter_label} (trimestre)</p>
                <p className="text-xl font-heading text-[#5a4a2a]" data-testid="vat-quarter">{fmt(taxes.vat_quarter)} EUR</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-[#5a4a2a]/40 mb-0.5">Anual</p>
                <p className="text-xl font-heading text-[#5a4a2a]" data-testid="vat-year">{fmt(taxes.vat_year)} EUR</p>
              </div>
            </div>
          </MetricCard>

          <MetricCard icon={Receipt} label="IRPF (20%)">
            <div className="flex justify-between items-end">
              <div>
                <p className="text-xs text-[#5a4a2a]/40 mb-0.5">{taxes.quarter_label} (trimestre)</p>
                <p className="text-xl font-heading text-[#5a4a2a]" data-testid="irpf-quarter">{fmt(taxes.irpf_quarter)} EUR</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-[#5a4a2a]/40 mb-0.5">Anual</p>
                <p className="text-xl font-heading text-[#5a4a2a]" data-testid="irpf-year">{fmt(taxes.irpf_year)} EUR</p>
              </div>
            </div>
          </MetricCard>
        </div>

        {/* Row 7: Expenses */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <MetricCard icon={CreditCard} label="Gastos Totales">
            <div className="flex justify-between items-end">
              <div>
                <p className="text-xs text-[#5a4a2a]/40 mb-0.5">Mensual</p>
                <p className="text-xl font-heading text-[#5a4a2a]" data-testid="expenses-month">{fmt(expenses.month_total)} EUR</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-[#5a4a2a]/40 mb-0.5">Anual</p>
                <p className="text-xl font-heading text-[#5a4a2a]" data-testid="expenses-year">{fmt(expenses.year_total)} EUR</p>
              </div>
            </div>
          </MetricCard>

          <MetricCard icon={CreditCard} label="Gastos Desgravables">
            <div className="flex justify-between items-end">
              <div>
                <p className="text-xs text-[#5a4a2a]/40 mb-0.5">Mensual</p>
                <p className="text-xl font-heading text-[#5a4a2a]" data-testid="deductible-month">{fmt(expenses.month_deductible)} EUR</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-[#5a4a2a]/40 mb-0.5">Anual</p>
                <p className="text-xl font-heading text-[#5a4a2a]" data-testid="deductible-year">{fmt(expenses.year_deductible)} EUR</p>
              </div>
            </div>
          </MetricCard>
        </div>

        {/* Row 8: Quick Actions */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { to: '/admin/finanzas', icon: Landmark, label: 'Finanzas' },
            { to: '/admin/formaciones', icon: BookOpen, label: 'Formaciones' },
            { to: '/admin/alumnos', icon: UserCog, label: 'Alumnas' },
            { to: '/admin/campus', icon: Flower2, label: 'Campus' },
            { to: '/admin/masterclass', icon: Sparkles, label: 'Masterclass' },
            { to: '/admin/paid-workshops', icon: Sparkles, label: 'Talleres pago' },
          ].map(a => (
            <Link key={a.to} to={a.to} className="flex items-center gap-2 rounded-xl border border-[#e8e2d8] bg-white px-4 py-3 hover:border-[#c9a227]/30 hover:bg-[#c9a227]/5 transition-all" data-testid={`quick-${a.label.toLowerCase()}`}>
              <a.icon className="w-4 h-4 text-[#c9a227]" strokeWidth={1.5} />
              <span className="text-sm text-[#5a4a2a]/70">{a.label}</span>
            </Link>
          ))}
        </div>

      </div>
    </div>
  );
}
